import { api } from './index'

/**
 * Windows the merchant-api accepts; anything else is a 400. Explicit
 * `query_start_date`/`query_end_date` win over the range when both are sent.
 */
export type OrderMetricsRange = 'custom' | 'all_time' | 'last_30'

/** Wire shape of GET /merchants/:merchant_id/order_metrics. */
export type OrderMetricsResponse = {
  orders_count: number
  // Integer cents, or null for a merchant with no orders in the window.
  average_check_cents: number | null
  // 30d OrderRating.down count (D9). Optional/additive: absent until the BE
  // ships it — treat absence the same as "not computable", never as 0.
  negative_rating_count?: number
}

export type OrderMetrics = {
  ordersCount: number
  averageCheckCents: number | null
  // null ⇔ the BE field is absent (not yet shipped) or the metric is
  // otherwise unavailable. A real zero is a computable 0, not null.
  negativeRatingCount: number | null
}

export type GetOrderMetricsParams = {
  range: OrderMetricsRange
  queryStartDate: string | null
  queryEndDate: string | null
}

// Relative suffix only — the `/merchants/:id` prefix is applied by the shared
// baseQuery (see api/utils.ts), so the merchant is not a query param.
export const orderMetricsQuery = ({
  range,
  queryStartDate,
  queryEndDate,
}: GetOrderMetricsParams) => ({
  url: 'order_metrics',
  params: {
    range,
    // Sent only when present: a null date would serialize as an empty param and
    // the backend would read it as an explicit (invalid) bound.
    ...(queryStartDate ? { query_start_date: queryStartDate } : {}),
    ...(queryEndDate ? { query_end_date: queryEndDate } : {}),
  },
})

export const transformOrderMetrics = (
  response: OrderMetricsResponse
): OrderMetrics => ({
  ordersCount: response.orders_count,
  averageCheckCents: response.average_check_cents,
  // `?? null`, not `|| null` — a real 0 must map to 0, not null.
  negativeRatingCount: response.negative_rating_count ?? null,
})

export const orderMetricsApi = api.injectEndpoints({
  endpoints: builder => ({
    getOrderMetrics: builder.query<OrderMetrics, GetOrderMetricsParams>({
      query: orderMetricsQuery,
      transformResponse: transformOrderMetrics,
      providesTags: ['OrderMetrics'],
      // This backs the promo page's ROI band (useMonthlyOrders), a
      // best-effort estimate on a marketing page the merchant can't act on
      // — an RBAC 403 here (shouldn't normally happen; the hook already
      // skips the request without `reporting` access, but a stale
      // permission cache or a race could still produce one) must not raise
      // baseQuery's danger toast the way it would for a page the error is
      // actually actionable on.
      extraOptions: { skipErrorToast: true },
    }),
  }),
})

export const { useGetOrderMetricsQuery } = orderMetricsApi
